/**
 * Inspect Student-Class Relationships
 * Shows which students are in which classes
 *
 * USAGE: await window.inspectStudentClasses()
 */

import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase.js';

async function inspectStudentClasses() {
  console.log('🔍 Inspecting student-class relationships...\n');

  try {
    // Get schoolId
    let schoolId = null;
    const keys = Object.keys(localStorage);
    const contextKey = keys.find(k => k.startsWith('swan_school_context_'));

    if (contextKey) {
      const contextData = localStorage.getItem(contextKey);
      if (contextData) {
        schoolId = JSON.parse(contextData).schoolId;
      }
    }

    if (!schoolId) {
      console.error('No schoolId found');
      return;
    }

    // Get all students
    const studentsRef = collection(db, `schools/${schoolId}/students`);
    const studentSnapshot = await getDocs(studentsRef);

    // Get all classes
    const classesRef = collection(db, `schools/${schoolId}/classes`);
    const classSnapshot = await getDocs(classesRef);

    // Build class map
    const classMap = {};
    classSnapshot.forEach(doc => {
      const cls = doc.data();
      classMap[doc.id] = cls.name || 'Unnamed';
    });

    console.log(`📚 Found ${classSnapshot.size} classes:`);
    Object.entries(classMap).forEach(([id, name]) => {
      console.log(`  - ${name} (${id})`);
    });

    // Analyze students
    const byStatus = {
      active: [],
      graduated: [],
      inactive: [],
      undefined: []
    };

    studentSnapshot.forEach(doc => {
      const student = doc.data();
      const status = student.status || 'undefined';
      byStatus[status].push({
        name: `${student.firstName} ${student.surname}`,
        classId: student.classId,
        className: student.className,
        status: status
      });
    });

    console.log(`\n👥 Found ${studentSnapshot.size} total students:\n`);

    // Show active students with classes
    console.log(`✅ ACTIVE Students (${byStatus.active.length}):`);
    const activeWithClass = byStatus.active.filter(s => s.classId);
    const activeWithoutClass = byStatus.active.filter(s => !s.classId);

    console.log(`  With classId (${activeWithClass.length}):`);
    activeWithClass.forEach(s => {
      const currentClassName = classMap[s.classId] || 'UNKNOWN CLASS';
      console.log(`    - ${s.name}: ${currentClassName} (${s.classId})`);
    });

    console.log(`  WITHOUT classId (${activeWithoutClass.length}):`);
    activeWithoutClass.forEach(s => {
      console.log(`    - ${s.name}: NO CLASS ASSIGNED`);
    });

    // Show graduated students still with classes
    const graduatedWithClass = byStatus.graduated.filter(s => s.classId);
    if (graduatedWithClass.length > 0) {
      console.log(`\n⚠️  Graduated with classId (${graduatedWithClass.length}):`);
      graduatedWithClass.forEach(s => {
        console.log(`    - ${s.name}: ${s.className} (${s.classId})`);
      });
    }

    // Summary
    console.log('\n📊 SUMMARY:');
    console.log(`  - Active students WITH classes: ${activeWithClass.length}`);
    console.log(`  - Active students WITHOUT classes: ${activeWithoutClass.length}`);
    console.log(`  - Graduated students still in classes: ${graduatedWithClass.length}`);

    return {
      classes: classMap,
      activeWithClass: activeWithClass.length,
      activeWithoutClass: activeWithoutClass.length,
      graduatedWithClass: graduatedWithClass.length
    };

  } catch (error) {
    console.error('❌ Error:', error);
    throw error;
  }
}

export { inspectStudentClasses };